import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, MapPin, Copy, CheckCheck } from "lucide-react";
import axios from "axios";
import OTPModal from "./OTPModal";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared order details form for Cart checkout.
 * Props:
 *   title        – modal heading
 *   itemLines    – array of strings, one per order line
 *   total        – optional total string, e.g. "Rs.528"
 *   cartItems    – raw cart items array for DB saving
 *   cartTotal    – numeric total for DB saving
 *   onClose      – called when user dismisses
 *   onConfirm    – called with { name, phone, address } to build WA message in parent
 */
export default function OrderFormModal({ title, itemLines = [], total, cartItems = [], cartTotal = 0, onClose, onConfirm }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [waUrl, setWaUrl] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showOTP, setShowOTP] = useState(false);

  const { isVerified, setVerified, customerHeader } = useCustomerAuth();
  const { clearCart } = useCart();
  const { t } = useLang();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const doSaveAndConfirm = async (freshToken = null) => {
    // Build auth header — use freshToken if just verified (state not updated yet)
    const authConfig = freshToken
      ? { headers: { Authorization: `Bearer ${freshToken}` } }
      : customerHeader();

    // Save order to DB if we have cart items
    let savedOrderId = null;
    if (cartItems.length > 0) {
      try {
        const res = await axios.post(`${API}/orders`, {
          customer_phone: form.phone,
          customer_name: form.name,
          items: cartItems.map(item => ({
            name: item.mushroom.name,
            uom: item.mushroom.uom || "250g",
            price: item.mushroom.price,
            qty: item.quantity
          })),
          total: cartTotal,
          address: form.address
        }, authConfig);
        savedOrderId = res.data.id;
        setOrderId(savedOrderId);
        clearCart();
      } catch (err) {
        console.error("Order save failed:", err);
      }
    }
    const url = onConfirm(form, savedOrderId);
    setWaUrl(url);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isVerified(form.phone)) {
      setShowOTP(true);
      return;
    }
    doSaveAndConfirm();
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D97A46]/30 transition-colors";
  const inputStyle = { backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', color: 'var(--c-text)' };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
        onClick={e => e.target === e.currentTarget && onClose()}
        data-testid="order-form-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="w-full max-w-md rounded-2xl border shadow-xl flex flex-col"
          style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', maxHeight: '90vh' }}
          data-testid="order-form-modal"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--c-border)' }}>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" style={{ color: '#25D366' }} />
              <h2 className="font-serif text-xl font-bold" style={{ color: 'var(--c-text)' }}>{t('order.title')}</h2>
            </div>
            <button onClick={onClose} data-testid="close-order-form-modal"
              className="p-1.5 rounded-full transition-colors hover:bg-gray-100" style={{ color: 'var(--c-muted)' }}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
          {!waUrl ? (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {itemLines.length > 0 && (
                <div className="p-3 rounded-xl border space-y-1" style={{ backgroundColor: 'var(--c-bg)', borderColor: 'var(--c-border)' }}>
                  {itemLines.map((line, i) => (
                    <p key={`line-${i}`} className="text-xs" style={{ color: 'var(--c-muted)' }}>{line}</p>
                  ))}
                  {total && (
                    <p className="text-sm font-semibold pt-1 border-t mt-1" style={{ color: '#D97A46', borderColor: 'var(--c-border)' }}>{t('cart.total')}: {total}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>{t('order.name')} *</label>
                <input className={inputCls} style={inputStyle} required value={form.name} onChange={e => set("name", e.target.value)}
                  placeholder={t('order.namePh')} data-testid="order-name-input" />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>{t('order.phone')} *</label>
                <input className={inputCls} style={inputStyle} required type="tel" maxLength={10}
                  value={form.phone} onChange={e => set("phone", e.target.value.replace(/\D/g, ""))}
                  placeholder={t('order.phonePh')} data-testid="order-phone-input" />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5 flex items-center gap-1" style={{ color: 'var(--c-muted)' }}>
                  <MapPin className="w-3 h-3" /> {t('order.address')} *
                </label>
                <textarea className={inputCls} style={inputStyle} required rows={3} value={form.address} onChange={e => set("address", e.target.value)}
                  placeholder={t('order.addressPh')}
                  data-testid="order-address-input" />
              </div>

              <button type="submit" data-testid="confirm-order-btn"
                className="w-full py-3.5 rounded-full font-semibold text-white flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={{ backgroundColor: '#25D366' }}>
                <MessageCircle className="w-5 h-5" />
                {t('order.confirm')}
              </button>
              <p className="text-center text-xs pb-2" style={{ color: 'var(--c-muted)' }}>
                {isVerified(form.phone) ? t('order.verifiedNote') : t('order.verifyNote')}
              </p>
            </form>
          ) : (
            <div className="p-8 text-center" data-testid="order-success-screen">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-serif text-xl font-bold mb-1" style={{ color: 'var(--c-text)' }}>{t('order.successTitle')}</h3>
              <p className="text-sm mb-4" style={{ color: 'var(--c-muted)' }}>{t('order.successMsg')}</p>

              {/* Order ID */}
              {orderId && (
                <div className="mb-5 p-3 rounded-xl border flex items-center justify-between gap-3"
                  style={{ backgroundColor: 'var(--c-bg)', borderColor: 'var(--c-border)' }}>
                  <div className="text-left">
                    <p className="text-xs font-medium uppercase tracking-wider mb-0.5" style={{ color: 'var(--c-muted)' }}>{t('order.orderId')}</p>
                    <p className="font-mono text-sm font-semibold" style={{ color: 'var(--c-text)' }} data-testid="success-order-id">
                      #{orderId.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(orderId.slice(0, 8).toUpperCase()); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: copied ? '#10b981' : 'var(--c-muted)', backgroundColor: copied ? 'rgba(16,185,129,0.1)' : 'transparent' }}
                    title="Copy Order ID" data-testid="copy-order-id-btn">
                    {copied ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}

              <a href={waUrl} target="_blank" rel="noreferrer" data-testid="open-whatsapp-order-btn"
                className="w-full mb-3 py-3.5 rounded-full font-semibold text-white flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:shadow-md transition-all"
                style={{ backgroundColor: '#25D366', display: 'flex' }}
              >
                <MessageCircle className="w-5 h-5" />
                {t('order.whatsapp')}
              </a>
              <button onClick={onClose}
                className="w-full px-6 py-2.5 rounded-full border text-sm font-medium transition-all mt-2 hover:bg-gray-50"
                style={{ borderColor: 'var(--c-border)', color: 'var(--c-muted)' }}>
                {t('order.close')}
              </button>
            </div>
          )}
          </div>
        </motion.div>
      </motion.div>

      {showOTP && (
        <OTPModal
          phone={form.phone}
          name={form.name}
          onVerified={(phone, name, token) => {
            setVerified(phone, name, token);
            setShowOTP(false);
            doSaveAndConfirm(token); // pass token directly — context state hasn't updated yet
          }}
          onClose={() => setShowOTP(false)}
        />
      )}
    </AnimatePresence>
  );
}
